import {Component, EventEmitter, Input, OnInit, ViewChild} from '@angular/core';
import {BsModalRef} from "ngx-bootstrap/modal";
import * as _ from 'lodash';
import {IsDirty} from "../../services/can-deactivate-guard/is-dirty.interface";
import {WarningModalService} from "../../services/warning-modal/warning-modal.service";
import {Observable, of} from "rxjs";
import {ProfileService} from "../../services/profile/profile.service";

@Component({
  selector: 'app-email-edit-modal',
  templateUrl: './email-edit-modal.component.pug',
  styleUrls: ['./email-edit-modal.component.scss'],
  standalone: false
})
export class EmailEditModalComponent implements OnInit, IsDirty {
  @Input() emails!: any[];
  public event: EventEmitter<any> = new EventEmitter();
  @ViewChild('emailEditForm', {static: false}) emailEditForm: any;
  newEmail: any;
  toUpdate: any = {'to_add': [], 'to_remove': [], 'new_primary': '', 'verify': []};
  messages = [];
  showValidationError = false;

  constructor(public _bsModalRef: BsModalRef,
              private modal: WarningModalService,
              private profile: ProfileService,) {
  }

  ngOnInit(): void {
  }

  cancel() {
    this.canDeactivate().subscribe(response => {
      if (response) {
        this.markAsPristine();
        this._bsModalRef.hide();
      }
    });
  }

  save() {
    this.messages = [];
    this.updateEmails(this.toUpdate);
    this.markAsPristine();
  }

  add() {
    this.showValidationError = true;
    if(!this.emailEditForm.valid) {
      this.showValidationError = true;
      return;
    }
    this.showValidationError = false;
    if (!_.find(this.emails, {'email_id': this.newEmail.toLowerCase()})) {
      this.toUpdate['to_add'].push(this.newEmail);
      this.save();
    } else {
      this.setMessages([{
        'type': 'danger',
        'message': $localize`Email already exists.`
      }])
    }
  }

  delete(email) {
    this.modal.confirm($localize`Remove email address?`, $localize`Are you sure you want to delete ${email['email_id']}?`, false,
      $localize`Cancel`, $localize`Yes`, true).subscribe(response => {
      if (response) {
        this.toUpdate['to_remove'].push(email);
        this.save();
      }
    });
  }

  makePrimary(email) {
    this.toUpdate['new_primary'] = email;
    this.save();
  }

  verify(email) {
    this.toUpdate['verify'].push(email);
    this.save();
  }

  isDirty(): boolean {
    return this.toUpdate['to_remove'].length > 0
      || this.toUpdate['to_add'].length > 0
      || this.toUpdate['new_primary'] !== ''
      || this.toUpdate['verify'].length > 0;
  }

  markAsPristine() {
    this.toUpdate = {'to_add': [], 'to_remove': [], 'new_primary': '', 'verify': []};
  }

  canDeactivate(): Observable<boolean> {
    // If component is dirty, warn user
    if (this.isDirty()) {
      return this.modal.confirm();
    } else {
      return of(true);
    }
  }

  setMessages(newMessages) {
    this.messages = this.messages.concat(newMessages);
  }

  private updateEmails(updates) {
    let save_data = false;
    let emails = _.cloneDeep(this.emails);
    if (updates['to_add'].length > 0) {
      for (let email of updates['to_add']) {
        emails.push({
          'email_id': email,
          'is_primary': false,
          'is_verified': false,
        });
      }
      save_data = true;
    }

    if (updates['to_remove'].length > 0) {
      for (let email of updates['to_remove']) {
        _.remove(emails, email);
      }
      save_data = true;
    }

    // Update emails
    if (save_data) {
      this.profile.updateEmail(emails).subscribe(data => {
        if (data['saved']) {
          this.emails = emails;
          for (let email of updates['to_add']) {
            this.setMessages([{
              type: 'success',
              message: $localize`Verification email has been sent to ${email}.`
            }]);
          }
          for (let email of updates['to_remove']) {
            this.setMessages([{
              type: 'success',
              message: $localize`${email['email_id']}:email_id: has been removed.`
            }]);
          }
          this.event.emit(this.emails);
        } else { // Error case
          console.log(data);
          this.setMessages(
            [
              {
                'type': 'danger',
                'message': $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
              }
            ]
          );
        }
      }, error => {
        console.log(error);
        if (!('errors' in error.error['data'])) {
          this.setMessages([{
            'type': 'danger',
            'message': $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
          }]);
          return;
        }
        let messages: Array<any> = [];
        for (let email of updates['to_add']) {
          for (const [key, value] of Object.entries(error.error['data']['errors'])) {
            if (key === "email" && value === "linked_to_another_user") {
              messages.push({
                'type': 'danger',
                'message': $localize`Oops! Something went wrong. ${email} can not be added to your profile.`
              });
            } else {
              messages.push({
                'type': 'danger',
                'message': $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
              });
            }
          }
        }
        this.setMessages(messages);
      });
    }

    // Make email primary
    if (updates['new_primary'] !== '') {
      let email = updates['new_primary'];
      this.profile.makeEmailPrimary({'email_id': email['email_id']}).subscribe(data => {
        if (data['saved']) {
          this.setMessages([{
            type: 'success',
            message: $localize`Your email address ${email['email_id']}:email_id: has been set as primary email address.`
          }]);
          _.forEach(this.emails, function (o) {
            o.is_primary = o.email_id === email.email_id;
          });
          this.event.emit(this.emails);
        } else { // Error case
          console.log(data);
          this.setMessages(
            [
              {
                'type': 'danger',
                'message': $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
              }
            ]
          );
        }
      }, error => {
        console.log(error);
        this.setMessages([{
          type: 'danger',
          message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
        }]);
      });
    }

    //  Verify email
    if (updates['verify'].length > 0) {
      for (let to_verify of updates['verify']) {
        let email = _.find(emails, to_verify);
        this.profile.verifyEmail(email).subscribe(data => {
          if (data['saved']) {
            console.log('Saved successfully');
            this.setMessages([{
              type: 'success',
              message: $localize`A verification email has been sent to ${email['email_id']}:email_id:.`
            }]);
          } else { // Error case
            console.log(data);
            this.setMessages(
              [
                {
                  'type': 'danger',
                  'message': $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
                }
              ]
            );
          }
        }, error => {
          console.log(error);
          this.setMessages([{
            type: 'danger',
            message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
          }]);
        });
      }
    }
  }
}
